// Locale-aware date & number formatting keyed off the active i18n language.
// Currency stays USD in both languages; dates/numbers follow the locale.
import i18n from "@/i18n";
import { es as dfnsEs, enUS as dfnsEnUS } from "date-fns/locale";
import type { Locale } from "date-fns";

export function currentLanguage(): "en" | "es" {
  return i18n.language === "es" ? "es" : "en";
}

/** BCP-47 locale string for Intl APIs / toLocaleDateString. */
export function intlLocale(): string {
  return currentLanguage() === "es" ? "es-US" : "en-US";
}

/** date-fns locale object for format()/formatDistance() calls. */
export function dateFnsLocale(): Locale {
  return currentLanguage() === "es" ? dfnsEs : dfnsEnUS;
}

/** Localized date, e.g. "Jul 23, 2026" / "23 jul 2026". */
export function fmtDate(d: Date | string | number | null | undefined, opts?: Intl.DateTimeFormatOptions): string {
  if (d == null || d === "") return "—";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(intlLocale(), opts ?? { month: "short", day: "numeric", year: "numeric" });
}

/** Localized date + time. */
export function fmtDateTime(d: Date | string | number | null | undefined): string {
  if (d == null || d === "") return "—";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(intlLocale(), {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

/** USD currency formatted for the active locale (currency stays USD). */
export function fmtCurrency(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString(intlLocale(), {
    style: "currency", currency: "USD",
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
}

/** Plain number with locale grouping. */
export function fmtNumber(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString(intlLocale(), {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
}
